import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { brandForSku, PACKAGE_ORDER, SKU_BRAND_MAP } from '../../lib/skuBrandVersion.js';

const STATUS_LABEL = Object.freeze({
  not_started: '未激活',
  pending_review: '待审核',
  active: '正常',
  rejected: '已拒绝',
  frozen: '冻结',
  archived: '归档',
  trial_expired: '试用到期',
});

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function expiryState(row) {
  const raw = row.expires_at || row.trial_expires_at;
  if (!raw) return { label: '未设置', className: 'text-slate-700' };
  const t = new Date(raw).getTime();
  if (!Number.isFinite(t)) return { label: '异常', className: 'text-rose-600' };
  const days = Math.ceil((t - Date.now()) / 86400000);
  if (days < 0) return { label: `已过期 ${Math.abs(days)} 天`, className: 'text-rose-600' };
  if (days <= 7) return { label: `${days} 天后到期`, className: 'text-amber-600' };
  return { label: `${days} 天后到期`, className: 'text-emerald-700' };
}

function statusBadge(status) {
  if (status === 'active') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status === 'trial_expired' || status === 'frozen') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (status === 'rejected' || status === 'archived') return 'bg-rose-50 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
}

function normalizeLayers(value, fallback = []) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return value.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }
  return Array.isArray(fallback) ? fallback : [];
}

function toErrorMessage(err, fallback) {
  if (err instanceof HttpError) return `${err.code}: ${err.message}`;
  return err?.message || fallback;
}

function MembershipPanel({ detail, onSaved }) {
  const [skuCode, setSkuCode] = useState('');
  const [period, setPeriod] = useState('1y');
  const [mode, setMode] = useState('reset');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('客户已付款，开通正式会员');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const current = detail?.workspace?.sku_code || 'sku_b1';
    setSkuCode(SKU_BRAND_MAP[current]?.sku_code ? current : 'sku_b1');
    setPeriod('1y');
    setMode('reset');
    setExpiresAt('');
    setNote('客户已付款，开通正式会员');
    setMessage(null);
  }, [detail?.workspace?.id]);

  const submit = async () => {
    if (!detail?.workspace?.id) return;
    setBusy(true);
    setMessage(null);
    try {
      const payload = {
        sku_code: skuCode,
        period,
        mode,
        note,
      };
      if (expiresAt.trim()) {
        payload.expires_at = expiresAt.trim();
      }
      const res = await api.post(`/api/ops/workspaces/${detail.workspace.id}/membership`, payload);
      setMessage(`已设置到期日：${formatDate(res.expires_at)}`);
      await onSaved();
    } catch (err) {
      setMessage(toErrorMessage(err, '保存失败'));
    } finally {
      setBusy(false);
    }
  };

  if (!detail) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-700">
        选择左侧客户后，可设置套餐、正式到期日，或赠送会员时间。
      </section>
    );
  }

  const ws = detail.workspace;
  const currentSku = brandForSku(ws.sku_code);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{ws.company_name || ws.name || `工作区 #${ws.id}`}</h2>
          <p className="mt-1 text-sm text-slate-700">
            负责人：{ws.owner_display_name || ws.owner_email || '—'} · 当前 {currentSku.shortName}
          </p>
        </div>
        <Link to={`/ops/workspaces/${ws.id}/plan`} className="rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
          套餐页
        </Link>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded border border-slate-200 bg-white p-3">
          <dt className="text-xs font-medium text-slate-700">正式会员到期</dt>
          <dd className="mt-1 font-medium text-slate-900">{formatDate(ws.expires_at)}</dd>
        </div>
        <div className="rounded border border-slate-200 bg-white p-3">
          <dt className="text-xs font-medium text-slate-700">试用到期</dt>
          <dd className="mt-1 font-medium text-slate-900">{formatDate(ws.trial_expires_at)}</dd>
        </div>
        <div className="rounded border border-slate-200 bg-white p-3">
          <dt className="text-xs font-medium text-slate-700">状态</dt>
          <dd className="mt-1 font-medium text-slate-900">{STATUS_LABEL[ws.registration_status] || ws.registration_status}</dd>
        </div>
        <div className="rounded border border-slate-200 bg-white p-3">
          <dt className="text-xs font-medium text-slate-700">成员</dt>
          <dd className="mt-1 font-medium text-slate-900">{detail.members?.length || 0} 人</dd>
        </div>
      </dl>

      <div className="mt-5 grid gap-3 md:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold text-slate-800">套餐</span>
          <select
            value={skuCode}
            onChange={(e) => setSkuCode(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
          >
            {PACKAGE_ORDER.filter((code) => code !== 'extra_seat').map((code) => (
              <option key={code} value={code}>{brandForSku(code).shortName}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold text-slate-800">有效期</span>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            disabled={Boolean(expiresAt.trim())}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-100 disabled:text-slate-700 disabled:opacity-100"
          >
            <option value="1y">一年</option>
            <option value="30d">赠送一个月</option>
            <option value="7d">赠送一周</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold text-slate-800">计算方式</span>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            disabled={Boolean(expiresAt.trim())}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-100 disabled:text-slate-700 disabled:opacity-100"
          >
            <option value="reset">从今天重新计算</option>
            <option value="extend">在现有到期日上延长</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold text-slate-800">指定到期日</span>
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
          />
        </label>
        <label className="block text-sm md:col-span-2">
          <span className="mb-1 block text-xs font-semibold text-slate-800">备注</span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500"
          />
        </label>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="text-xs text-slate-700">
          保存后写入工作区到期日和审计日志，客户刷新后生效。
        </div>
        <button
          type="button"
          disabled={busy || note.trim().length < 4}
          onClick={submit}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? '保存中…' : '保存会员'}
        </button>
      </div>
      {message && <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{message}</div>}

      <div className="mt-6">
        <h3 className="mb-2 text-sm font-semibold text-slate-800">工作区用户</h3>
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
            <thead className="bg-slate-50 text-xs font-semibold text-slate-700">
              <tr>
                <th className="px-3 py-2">姓名/邮箱</th>
                <th className="px-3 py-2">角色</th>
                <th className="px-3 py-2">手机号</th>
                <th className="px-3 py-2">席位</th>
                <th className="px-3 py-2">状态</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {(detail.members || []).map((m) => (
                <tr key={m.membership_id}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{m.display_name || m.email}</div>
                    <div className="font-mono text-xs text-slate-700">{m.email}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-800">{m.member_role}</td>
                  <td className="px-3 py-2 text-slate-800">{m.phone || '—'}</td>
                  <td className="px-3 py-2 text-slate-800">{m.seat_type}</td>
                  <td className="px-3 py-2 text-slate-800">{m.member_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default function OpsCustomers() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '100');
    if (q.trim()) params.set('q', q.trim());
    if (status) params.set('status', status);
    return params.toString();
  }, [q, status]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/ops/workspaces?${queryString}`);
      const items = Array.isArray(res?.items) ? res.items : [];
      setRows(items);
      setTotal(Number(res?.total || items.length));
      setSelectedId((current) => {
        if (!current) return items[0]?.id || null;
        return items.some((r) => r.id === current) ? current : (items[0]?.id || null);
      });
    } catch (err) {
      setError(toErrorMessage(err, '加载客户失败'));
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  const loadDetail = useCallback(async () => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await api.get(`/api/ops/workspaces/${selectedId}`);
      setDetail(res);
    } catch (err) {
      setError(toErrorMessage(err, '加载客户详情失败'));
    } finally {
      setDetailLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const refreshAll = async () => {
    await loadRows();
    await loadDetail();
  };

  const bulkGift = async (period) => {
    const label = period === '7d' ? '一周' : '一个月';
    setBulkBusy(true);
    setBulkMsg(null);
    try {
      const payload = {
        period,
        status: status || 'active,not_started,pending_review,trial_expired',
        q: q.trim(),
        note: `运营统一赠送${label}会员`,
      };
      if (!payload.q) {
        payload.workspace_ids = rows.map((r) => r.id);
      }
      const res = await api.post('/api/ops/workspaces/bulk-membership-gift', payload);
      setBulkMsg(`已赠送${label}，影响 ${res.affected_count || 0} 个工作区`);
      await refreshAll();
    } catch (err) {
      setBulkMsg(toErrorMessage(err, '批量赠送失败'));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-7xl px-6 py-6">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <Link to="/ops/dashboard" className="text-xs text-slate-700 hover:text-slate-900">← 返回控制台</Link>
            <h1 className="mt-1 text-xl font-semibold text-slate-900">客户 / 用户管理</h1>
            <p className="mt-1 text-sm text-slate-700">查看所有工作区、负责人、成员、套餐和会员到期日。</p>
          </div>
          <button type="button" onClick={refreshAll} className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50">
            刷新
          </button>
        </div>

        <div className="mb-4 grid gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-[1fr_180px_auto_auto]">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜公司、负责人邮箱、手机号或工作区 ID"
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
          >
            <option value="">全部状态</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={bulkBusy || rows.length === 0}
            onClick={() => bulkGift('7d')}
            className="rounded border border-blue-300 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          >
            统一送一周
          </button>
          <button
            type="button"
            disabled={bulkBusy || rows.length === 0}
            onClick={() => bulkGift('30d')}
            className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            统一送一个月
          </button>
        </div>

        {bulkMsg && <div className="mb-4 rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">{bulkMsg}</div>}
        {error && <div className="mb-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_520px]">
          <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div className="text-sm font-semibold text-slate-800">客户工作区</div>
              <div className="text-xs font-medium text-slate-700">共 {total} 个</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold text-slate-700">
                  <tr>
                    <th className="px-4 py-3">客户</th>
                    <th className="px-4 py-3">负责人</th>
                    <th className="px-4 py-3">套餐</th>
                    <th className="px-4 py-3">到期</th>
                    <th className="px-4 py-3">状态</th>
                    <th className="px-4 py-3">用户</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {loading && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-700">加载中…</td></tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-700">暂无客户</td></tr>
                  )}
                  {!loading && rows.map((row) => {
                    const exp = expiryState(row);
                    const sku = brandForSku(row.sku_code);
                    const active = row.id === selectedId;
                    return (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedId(row.id)}
                        className={active ? 'cursor-pointer bg-blue-100/80' : 'cursor-pointer hover:bg-slate-100'}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{row.company_name || row.name || `工作区 #${row.id}`}</div>
                          <div className="font-mono text-xs text-slate-700">#{row.id} · {formatDateTime(row.created_at)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-900">{row.owner_display_name || row.owner_email || '—'}</div>
                          <div className="font-mono text-xs text-slate-700">{row.owner_phone || row.owner_email || '—'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{sku.shortName}</div>
                          <div className="text-xs text-slate-700">{normalizeLayers(row.enabled_layers, sku.layers).join(' + ') || '—'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-900">{formatDate(row.expires_at || row.trial_expires_at)}</div>
                          <div className={`text-xs ${exp.className}`}>{exp.label}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${statusBadge(row.registration_status)}`}>
                            {STATUS_LABEL[row.registration_status] || row.registration_status}
                          </span>
                          {row.audit_flag && <div className="mt-1 text-xs text-slate-700">{row.audit_flag}</div>}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {row.active_member_count ?? row.member_count ?? 0}
                          <span className="ml-1 text-xs text-slate-600">人</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <div className="space-y-4">
            {detailLoading ? (
              <section className="rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-700">加载详情中…</section>
            ) : (
              <MembershipPanel detail={detail} onSaved={refreshAll} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
